/**
 * CashBuyersFunnelCard — admin widget tracking the /cash-buyers paid-traffic
 * landing page. Shows views, total clicks, CTR, and a per-slot breakdown
 * with the top-converting slot highlighted, plus UTM-source breakdown.
 *
 * Backend: GET /api/admin/cash-buyers-funnel?days=30
 */
import { useEffect, useState } from "react";
import { Flame, MousePointerClick, Eye, TrendingUp, Award } from "lucide-react";
import { api } from "@/lib/api";

const WINDOWS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

const SLOT_LABELS = {
  "hero": "Hero CTA",
  "hero-secondary": "Hero (secondary)",
  "pricing-teaser": "Pricing teaser",
  "final": "Final CTA",
};

export default function CashBuyersFunnelCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/cash-buyers-funnel?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const topSlotKey = data?.top_slot?.slot;
  const maxClicks = data?.slots?.[0]?.clicks || 1;

  return (
    <div
      className="card p-5 mt-6 bg-gradient-to-br from-amber-50/40 to-white border-amber-200"
      data-testid="admin-cash-buyers-funnel-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Flame size={18} className="text-amber-500" />
          <div className="font-display font-extrabold text-lg text-slate-900">
            /cash-buyers · Paid traffic
          </div>
          <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
            A/B Slots
          </span>
        </div>
        <div className="flex gap-1" data-testid="admin-cash-buyers-window">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => setDays(w.value)}
              className={
                "text-xs font-bold px-2.5 py-1 rounded-md transition " +
                (days === w.value
                  ? "bg-amber-500 text-white"
                  : "bg-white text-slate-700 hover:bg-amber-100 border border-slate-200")
              }
              data-testid={`admin-cash-buyers-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
      ) : !data || data.views === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          No traffic on <code>/cash-buyers</code> in this window yet — drive a campaign or paste{" "}
          <code>?utm_source=fb&utm_campaign=test</code> to it to start tracking.
        </div>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat
              icon={Eye} label="Views" value={data.views.toLocaleString()}
              tone="slate" testid="cb-stat-views"
            />
            <Stat
              icon={MousePointerClick} label="Clicks" value={data.clicks.toLocaleString()}
              tone="emerald" testid="cb-stat-clicks"
            />
            <Stat
              icon={TrendingUp} label="CTR" value={`${data.ctr_pct}%`}
              tone={data.ctr_pct >= 5 ? "emerald" : data.ctr_pct >= 2 ? "amber" : "rose"}
              testid="cb-stat-ctr"
            />
          </div>

          {/* Top slot winner banner */}
          {data.top_slot && (
            <div
              className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-emerald-50 border border-emerald-200"
              data-testid="cb-top-slot-winner"
            >
              <Award size={18} className="text-emerald-600" />
              <div className="text-sm">
                <span className="text-slate-700">Top-converting slot:</span>{" "}
                <span className="font-bold text-emerald-700">
                  {SLOT_LABELS[data.top_slot.slot] || data.top_slot.slot}
                </span>{" "}
                <span className="text-slate-500">
                  ({data.top_slot.clicks} clicks · {data.top_slot.share_pct}% share)
                </span>
              </div>
            </div>
          )}

          {/* Per-slot bars */}
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">
            Clicks by CTA slot
          </div>
          <div className="space-y-2 mb-4">
            {data.slots.map((s) => {
              const isWinner = s.slot === topSlotKey;
              const widthPct = Math.max(2, (s.clicks / maxClicks) * 100);
              return (
                <div key={s.slot} className="flex items-center gap-3" data-testid={`cb-slot-${s.slot}`}>
                  <div className="w-32 shrink-0 text-xs font-semibold text-slate-700 truncate">
                    {SLOT_LABELS[s.slot] || s.slot}
                  </div>
                  <div className="flex-1 h-6 rounded bg-slate-100 overflow-hidden relative">
                    <div
                      className={
                        "h-full rounded transition-all " +
                        (isWinner ? "bg-emerald-500" : "bg-slate-400")
                      }
                      style={{ width: `${widthPct}%` }}
                    ></div>
                  </div>
                  <div className="w-20 text-right text-xs font-bold text-slate-900">
                    {s.clicks} <span className="text-slate-400 font-medium">· {s.share_pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* UTM source breakdown */}
          {data.sources?.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2 pt-2 border-t border-slate-200">
                Traffic sources
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.sources.map((src) => (
                  <span
                    key={src.source}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[11px] text-slate-700"
                    data-testid={`cb-source-${src.source}`}
                  >
                    <strong>{src.source}</strong> · {src.events}
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid={testid}>
      <div className={"inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold " + tones[tone]}>
        <Icon size={11} /> {label}
      </div>
      <div className="font-display font-extrabold text-2xl text-slate-900 mt-1">{value}</div>
    </div>
  );
}
